import { createTheme, keyframes, alpha } from '@mui/material/styles';
import { mobileThemeOverrides } from './mobileTheme';

// 🎨 Animation keyframes for delightful interactions
export const animations = {
  fadeIn: keyframes`
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  `,
  
  slideInLeft: keyframes`
    from { opacity: 0; transform: translateX(-30px); }
    to { opacity: 1; transform: translateX(0); }
  `,
  
  slideInRight: keyframes`
    from { opacity: 0; transform: translateX(30px); }
    to { opacity: 1; transform: translateX(0); }
  `,
  
  bounceIn: keyframes`
    0% { opacity: 0; transform: scale(0.3); }
    50% { transform: scale(1.05); }
    70% { transform: scale(0.9); }
    100% { opacity: 1; transform: scale(1); }
  `,
  
  pulse: keyframes`
    0% { transform: scale(1); }
    50% { transform: scale(1.05); }
    100% { transform: scale(1); }
  `,
  
  shimmer: keyframes`
    0% { background-position: -200px 0; }
    100% { background-position: calc(200px + 100%) 0; }
  `,
  
  wobble: keyframes`
    0% { transform: rotate(0deg); }
    15% { transform: rotate(-5deg); }
    30% { transform: rotate(3deg); }
    45% { transform: rotate(-3deg); }
    60% { transform: rotate(2deg); }
    75% { transform: rotate(-1deg); }
    100% { transform: rotate(0deg); }
  `,
  
  heartbeat: keyframes`
    0% { transform: scale(1); }
    14% { transform: scale(1.3); }
    28% { transform: scale(1); }
    42% { transform: scale(1.3); }
    70% { transform: scale(1); }
  `,
  
  float: keyframes`
    0% { transform: translateY(0px); }
    50% { transform: translateY(-20px); }
    100% { transform: translateY(0px); }
  `,
  
  rotate: keyframes`
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  `,
  
  glow: keyframes`
    from { box-shadow: 0 0 5px rgba(99, 102, 241, 0.5); }
    to { box-shadow: 0 0 20px rgba(99, 102, 241, 0.8), 0 0 30px rgba(99, 102, 241, 0.4); }
  `
};

// 🇨🇿 Czech-inspired color palette with modern gradients
export const czechColors = {
  red: '#dc143c',     // Czech flag red
  blue: '#11457e',    // Czech flag blue
  white: '#ffffff',   // Czech flag white
  
  // Modern gradients inspired by Czech heritage
  pragueGradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  moldauGradient: 'linear-gradient(135deg, #06b6d4 0%, #3b82f6 100%)',
  castleGradient: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
  forestGradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
  sunsetGradient: 'linear-gradient(135deg, #ec4899 0%, #8b5cf6 100%)',
};

// Create base theme first
const baseTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#6366f1', // Modern indigo
      light: '#8b5cf6',
      dark: '#4f46e5',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#ec4899', // Vibrant pink
      light: '#f472b6',
      dark: '#db2777',
      contrastText: '#ffffff',
    },
    error: {
      main: '#ef4444',
      light: '#f87171',
      dark: '#dc2626',
      contrastText: '#ffffff',
    },
    warning: {
      main: '#f59e0b',
      light: '#fbbf24',
      dark: '#d97706',
      contrastText: '#ffffff',
    },
    success: {
      main: '#10b981',
      light: '#34d399',
      dark: '#059669',
      contrastText: '#ffffff',
    },
    info: {
      main: '#06b6d4',
      light: '#22d3ee',
      dark: '#0891b2',
      contrastText: '#ffffff',
    },
    background: {
      default: '#f8fafc',
      paper: '#ffffff',
    },
    text: {
      primary: '#1e293b',
      secondary: '#64748b',
    },
    divider: alpha('#e2e8f0', 0.6),
  },
  
  // 🎨 Modern typography with Czech character support
  typography: {
    fontFamily: '"Inter", "Roboto", "Arial", sans-serif',
    h1: {
      fontSize: 'clamp(2rem, 5vw, 3.5rem)',
      fontWeight: 700,
      lineHeight: 1.2,
      letterSpacing: '-0.02em',
      background: czechColors.pragueGradient,
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
    },
    h2: {
      fontSize: 'clamp(1.5rem, 4vw, 2.5rem)',
      fontWeight: 600,
      lineHeight: 1.3,
      letterSpacing: '-0.01em',
    },
    h3: {
      fontSize: 'clamp(1.25rem, 3vw, 2rem)',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h4: {
      fontSize: 'clamp(1.125rem, 2.5vw, 1.5rem)',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h5: {
      fontSize: 'clamp(1rem, 2vw, 1.25rem)',
      fontWeight: 600,
      lineHeight: 1.5,
    },
    h6: {
      fontSize: 'clamp(0.875rem, 1.5vw, 1.125rem)',
      fontWeight: 600,
      lineHeight: 1.5,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
      letterSpacing: '0.00938em',
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.6,
      letterSpacing: '0.01071em',
    },
    button: {
      fontSize: '0.875rem',
      fontWeight: 600,
      letterSpacing: '0.02857em',
      textTransform: 'none' as const,
    },
  },
  
  // 🎯 Custom breakpoints for responsive design
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
  
  // ✨ Modern spacing for better visual hierarchy
  spacing: 8,
  
  // 🎪 Custom shadows for depth and elevation
  shadows: [
    'none',
    '0px 2px 4px rgba(0, 0, 0, 0.05)',
    '0px 4px 6px rgba(0, 0, 0, 0.05)',
    '0px 4px 8px rgba(0, 0, 0, 0.08)',
    '0px 6px 12px rgba(0, 0, 0, 0.08)',
    '0px 8px 16px rgba(0, 0, 0, 0.1)',
    '0px 12px 24px rgba(0, 0, 0, 0.1)',
    '0px 16px 32px rgba(0, 0, 0, 0.12)',
    '0px 24px 48px rgba(0, 0, 0, 0.15)',
    '0px 32px 64px rgba(0, 0, 0, 0.2)',
    // Enhanced shadows for special effects
    '0px 4px 20px rgba(99, 102, 241, 0.15)',
    '0px 8px 30px rgba(99, 102, 241, 0.2)',
    '0px 12px 40px rgba(99, 102, 241, 0.25)',
    '0px 16px 50px rgba(99, 102, 241, 0.3)',
    '0px 20px 60px rgba(99, 102, 241, 0.35)',
    '0px 24px 70px rgba(99, 102, 241, 0.4)',
    '0px 32px 80px rgba(99, 102, 241, 0.45)',
    '0px 40px 90px rgba(99, 102, 241, 0.5)',
    '0px 48px 100px rgba(99, 102, 241, 0.55)',
    '0px 56px 110px rgba(99, 102, 241, 0.6)',
    '0px 64px 120px rgba(99, 102, 241, 0.65)',
    '0px 72px 130px rgba(99, 102, 241, 0.7)',
    '0px 80px 140px rgba(99, 102, 241, 0.75)',
    '0px 88px 150px rgba(99, 102, 241, 0.8)',
    '0px 96px 160px rgba(99, 102, 241, 0.85)',
  ],
  
  // 🎭 Component style overrides for consistent design
  components: {
    // 🔲 Button components with animations
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          padding: '12px 24px',
          fontSize: '0.875rem',
          fontWeight: 600,
          textTransform: 'none',
          boxShadow: 'none',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          position: 'relative',
          overflow: 'hidden',
          
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: '-100%',
            width: '100%',
            height: '100%',
            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
            transition: 'left 0.5s',
          },
          
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0px 8px 25px rgba(99, 102, 241, 0.25)',
            
            '&::before': {
              left: '100%',
            },
          },
          
          '&:active': {
            transform: 'translateY(0)',
          },
          
          '@media (max-width:600px)': {
            padding: '10px 20px',
            fontSize: '0.8rem',
            minHeight: '44px',
          },
        },
        
        contained: {
          background: czechColors.pragueGradient,
          color: '#ffffff',
          
          '&:hover': {
            background: czechColors.sunsetGradient,
          },
        },
        
        outlined: {
          borderColor: alpha('#6366f1', 0.5),
          color: '#6366f1',
          borderWidth: 2,
          
          '&:hover': {
            borderColor: '#6366f1',
            backgroundColor: alpha('#6366f1', 0.05),
            borderWidth: 2,
          },
        },
        
        text: {
          color: '#6366f1',
          
          '&:hover': {
            backgroundColor: alpha('#6366f1', 0.05),
          },
        },
      },
    },
    
    // 🎴 Card components with modern styling
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          border: '1px solid rgba(255, 255, 255, 0.2)',
          background: 'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(20px)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          position: 'relative',
          overflow: 'hidden',
          
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '3px',
            background: czechColors.pragueGradient,
            transform: 'scaleX(0)',
            transformOrigin: 'left',
            transition: 'transform 0.3s ease',
          },
          
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0px 20px 40px rgba(0, 0, 0, 0.1)',
            
            '&::before': {
              transform: 'scaleX(1)',
            },
          },
        },
      },
    },
    
    // 📄 Paper components
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          border: '1px solid rgba(255, 255, 255, 0.15)',
          background: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(10px)',
        },
      },
    },
    
    // 📝 Text field components
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 12,
            transition: 'all 0.3s ease',
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            
            '&:hover': {
              transform: 'translateY(-1px)',
              boxShadow: '0px 4px 12px rgba(99, 102, 241, 0.15)',
            },
            
            '&.Mui-focused': {
              transform: 'translateY(-2px)',
              boxShadow: '0px 8px 20px rgba(99, 102, 241, 0.2)',
            },
            
            '@media (max-width:600px)': {
              fontSize: '16px', // Prevent iOS zoom
            },
          },
        },
      },
    },
    
    // 🏷️ Chip components
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontSize: '0.75rem',
          fontWeight: 600,
          transition: 'all 0.2s ease',
          
          '&:hover': {
            transform: 'scale(1.05)',
          },
          
          '@media (max-width:600px)': {
            fontSize: '0.7rem',
            height: '24px',
          },
        },
        
        colorPrimary: {
          background: czechColors.pragueGradient,
          color: '#ffffff',
        },
        
        colorSecondary: {
          background: czechColors.sunsetGradient,
          color: '#ffffff',
        },
        
        colorSuccess: {
          background: czechColors.forestGradient,
          color: '#ffffff',
        },
      },
    },
    
    // 🎯 Icon button components
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          
          '&:hover': {
            transform: 'rotate(5deg) scale(1.1)',
            backgroundColor: alpha('#6366f1', 0.1),
          },
          
          '@media (max-width:600px)': {
            minHeight: '44px',
            minWidth: '44px',
          },
        },
      },
    },
    
    // 📦 Container components
    MuiContainer: {
      styleOverrides: {
        root: {
          animation: `${animations.fadeIn} 0.6s ease-out`,
          
          '@media (max-width:600px)': {
            paddingLeft: '16px',
            paddingRight: '16px',
          },
        },
      },
    },
    
    // 🎪 App Bar components
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: czechColors.pragueGradient,
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
          backdropFilter: 'blur(20px)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          
          '@media (max-width:600px)': {
            '& .MuiToolbar-root': {
              minHeight: '56px',
              paddingLeft: '16px',
              paddingRight: '16px',
            },
          },
        },
      },
    },
    
    // 🗂️ Tab components
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.875rem',
          borderRadius: '8px 8px 0 0',
          transition: 'all 0.3s ease',
          
          '&:hover': {
            backgroundColor: alpha('#6366f1', 0.05),
            transform: 'translateY(-2px)',
          },
          
          '&.Mui-selected': {
            color: '#6366f1',
            position: 'relative',
            
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: '3px',
              background: czechColors.pragueGradient,
              borderRadius: '2px 2px 0 0',
            },
          },
        },
      },
    },
    
    // 📊 Linear Progress components
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          height: 8,
          overflow: 'hidden',
        },
        
        bar: {
          background: czechColors.pragueGradient,
          borderRadius: 4,
          
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            bottom: 0,
            right: 0,
            background: `linear-gradient(90deg, 
              transparent 0%, 
              rgba(255,255,255,0.3) 50%, 
              transparent 100%)`,
            animation: `${animations.shimmer} 2s infinite`,
          },
        },
      },
    },
    
    // ⚙️ Circular Progress components  
    MuiCircularProgress: {
      styleOverrides: {
        root: {
          animation: `${animations.pulse} 2s ease-in-out infinite`,
        },
        
        circle: {
          stroke: 'url(#gradient)',
        },
      },
    },
    
    // 🎭 Dialog components
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 16,
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          animation: `${animations.bounceIn} 0.5s ease-out`,
        },
      },
    },
    
    // 🎪 Tooltip components
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: 'rgba(30, 41, 59, 0.9)',
          backdropFilter: 'blur(8px)',
          borderRadius: 8,
          fontSize: '0.75rem',
          fontWeight: 500,
          padding: '8px 12px',
          animation: `${animations.fadeIn} 0.2s ease-out`,
        },
        
        arrow: {
          color: 'rgba(30, 41, 59, 0.9)',
        },
      },
    },
  },
});

// Now apply mobile theme overrides to the base theme
export const modernTheme = createTheme(baseTheme, mobileThemeOverrides(baseTheme));

export default modernTheme;

